export type ConvertArgs<Unit extends string, ToUnit extends Unit> = {
    value: number;
    from: Unit;
    to: ToUnit;
};

export abstract class UnitConverter<Unit extends string, BaseUnit extends Unit> {
    abstract readonly baseUnit: BaseUnit;
    abstract readonly units: readonly Unit[];

    readonly defaultViewUnit?: string;

    abstract toBase(args: { value: number; from: Unit }): { value: number; unit: BaseUnit };
    abstract convert<ToUnit extends Unit>(args: ConvertArgs<Unit, ToUnit>): { value: number; unit: ToUnit };

    /**
     * The threshold at which to switch to the next unit (exclusive upper bound).
     * For example, if set to 2000, 1999 will stay in the current unit, 2000 will switch to the next.
     */
    protected unitSwitchThreshold = 2000;

    toBest({ value, from }: { value: number; from: Unit }): { value: number; unit: Unit } {
        const converted = this.getAvailableBestUnits().map((du) => {
            const { value: convertedValue } = this.convert({ value, from, to: du });
            return { unit: du, value: convertedValue };
        });

        const candidates = converted.filter(({ value }) => Math.abs(value) >= 1);
        if (candidates.length > 0) {
            return candidates.reduce((prev, curr) => (Math.abs(curr.value) < Math.abs(prev.value) ? curr : prev));
        }

        return converted.reduce((prev, curr) => (Math.abs(curr.value) > Math.abs(prev.value) ? curr : prev));
    }

    getDisplayUnits() {
        return this.units;
    }

    protected getAvailableBestUnits() {
        return this.getDisplayUnits();
    }
}
